"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Gamepad2, Trophy, Users, ChevronRight, Star, Zap, Shield, ArrowRight } from "lucide-react"
import { Button } from "./components/ui/button"
import { Card, CardContent } from "./components/ui/card"

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false)
  const navigate = useNavigate()
  const [current, setCurrent] = useState(0)

  const images = [
    require("./assets/rivalsOnComputer.png"),
    require("./assets/rivalsAllBets.png"),
    require("./assets/rivalsStats.png"), // add more slides as needed
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Auto-slide every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length)
    }, 4000)
    return () => clearInterval(timer)
  }, [images.length])

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? "bg-black/90 backdrop-blur-md py-3 shadow-lg" : "bg-transparent py-5"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold">
                Rivals <span className="text-emerald-400">Sportsbook</span>
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              {/* <Link href="#features" className="text-gray-300 hover:text-white transition-colors">
                Features
              </Link>
              <Link href="#how-it-works" className="text-gray-300 hover:text-white transition-colors">
                How It Works
              </Link>
              <Link href="#testimonials" className="text-gray-300 hover:text-white transition-colors">
                Testimonials
              </Link> */}
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={()=>{navigate("/signin")}} variant="outline" className="border-gray-700 hover:bg-gray-800 hover:text-white">
                Log In
              </Button>
              <Button onClick={()=>{navigate("/signup")}} className="bg-emerald-500 hover:bg-emerald-600 text-white">Sign Up</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-10 md:pt-40 md:pb-10">
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 -left-40 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block px-3 py-1 rounded-full bg-gray-800 text-emerald-400 text-sm font-medium mb-4">
              The Future of Sports Betting
            </div>

            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight">
              JOIN RIVALS <span className="text-emerald-400">SPORTSBOOK</span>
              <div className="mt-2 bg-gradient-to-r from-blue-400 via-emerald-400 to-yellow-400 text-transparent bg-clip-text">
                BET AGAINST OTHER PEOPLE
              </div>
            </h1>

            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Start betting, challenge friends, and compete with sports fans worldwide on our next-generation social
              betting platform.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                onClick={()=>{navigate("/signup")}}
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 group"
              >
                Get Started
                <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button onClick={()=>{navigate("/signup")}} size="lg" variant="outline" className="border-gray-700 hover:bg-gray-800">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Slideshow Section */}
      <section className="py-24 pt-0 relative">
        <div className="relative backdrop-blur-sm rounded-3xl p-4 shadow-2xl flex justify-center items-center">
          <div className="relative w-4/5  aspect-video overflow-hidden rounded-2xl">
            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Rivals Sportsbook Slide ${i + 1}`}
                className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-700 rounded-2xl ${
                  current === i ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}

            {/* Navigation buttons */}
            <button
              onClick={() => setCurrent((current - 1 + images.length) % images.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 text-white p-3 rounded-full hover:bg-black/70 transition"
            >
              ‹
            </button>
            <button
              onClick={() => setCurrent((current + 1) % images.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 text-white p-3 rounded-full hover:bg-black/70 transition"
            >
              ›
            </button>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="py-24 relative" id="how-it-works">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <div className="inline-block px-3 py-1 rounded-full bg-gray-800 text-emerald-400 text-sm font-medium mb-4">
                  Mobile Experience
                </div>
                <h2 className="text-4xl font-bold mb-6">Bet Anytime, Anywhere</h2>
                <p className="text-xl text-gray-300">
                  Our intuitive mobile app makes sports betting social, fun, and accessible. Challenge friends and track
                  your bets in real-time.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 p-3 rounded-lg">
                    <Zap className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Lightning Fast</h3>
                    <p className="text-gray-400">Place bets in seconds with our streamlined interface.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 p-3 rounded-lg">
                    <Users className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Social Betting</h3>
                    <p className="text-gray-400">Create private leagues and compete with friends.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-emerald-500/20 p-3 rounded-lg">
                    <Shield className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Secure Platform</h3>
                    <p className="text-gray-400">Advanced security measures to protect your data and funds.</p>
                  </div>
                </div>
              </div>

              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">Download App</Button>
            </div>

            <div className="relative flex justify-center">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 rounded-full blur-3xl transform -translate-y-1/4 pointer-events-none -z-10"></div>
              <div className="relative bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-3xl p-4 shadow-2xl">
                <img
                  src={require('./assets/rivalsOnIphone.png')}                  width={300}
                  height={600}
                  alt="Rivals Sportsbook App"
                  className="rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="py-12 bg-gray-900/50 backdrop-blur-sm border-y border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <p className="text-4xl font-bold text-emerald-400">500K+</p>
              <p className="text-gray-400">Active Users</p>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-bold text-emerald-400">$10M+</p>
              <p className="text-gray-400">Payouts</p>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-bold text-emerald-400">98%</p>
              <p className="text-gray-400">Satisfaction</p>
            </div>
            <div className="space-y-2">
              <p className="text-4xl font-bold text-emerald-400">24/7</p>
              <p className="text-gray-400">Support</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Feature Cards */}
      <section className="py-24 relative" id="features">
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-3 py-1 rounded-full bg-gray-800 text-emerald-400 text-sm font-medium mb-4">
              Key Features
            </div>
            <h2 className="text-4xl font-bold mb-6">Why Choose Rivals Sportsbook</h2>
            <p className="text-xl text-gray-300">
              Our platform offers unique features designed to enhance your sports betting experience.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-emerald-500/10 hover:shadow-lg">
              <CardContent className="p-8">
                <Trophy className="w-12 h-12 text-emerald-400 mb-6" />
                <h3 className="text-2xl font-semibold mb-3">Compete & Win</h3>
                <p className="text-gray-300">
                  Challenge friends and win big with our competitive betting platform and leaderboards.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-emerald-500/10 hover:shadow-lg">
              <CardContent className="p-8">
                <Users className="w-12 h-12 text-emerald-400 mb-6" />
                <h3 className="text-2xl font-semibold mb-3">Social Betting</h3>
                <p className="text-gray-300">
                  Connect with friends and bet together on your favorite sports in private or public leagues.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-emerald-500/10 hover:shadow-lg">
              <CardContent className="p-8">
                <Gamepad2 className="w-12 h-12 text-emerald-400 mb-6" />
                <h3 className="text-2xl font-semibold mb-3">Easy to Play</h3>
                <p className="text-gray-300">
                  Simple and intuitive interface makes betting fun and accessible for beginners and experts.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-900/50 backdrop-blur-sm border-y border-gray-800" id="testimonials">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-3 py-1 rounded-full bg-gray-800 text-emerald-400 text-sm font-medium mb-4">
              Testimonials
            </div>
            <h2 className="text-4xl font-bold mb-6">What Our Users Say</h2>
            <p className="text-xl text-gray-300">
              Join thousands of satisfied users who love the Rivals Sportsbook experience.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-gray-800/30 backdrop-blur-sm border-gray-700">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6">
                  "Rivals Sportsbook has completely changed how I bet on sports. The social features make it so much
                  more fun!"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gray-700 mr-4"></div>
                  <div>
                    <p className="font-semibold">John Smith</p>
                    <p className="text-gray-400 text-sm">Sports Enthusiast</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/30 backdrop-blur-sm border-gray-700">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6">
                  "I love tracking bets with friends. The interface is clean and fast—makes game days even better. {}"
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gray-700 mr-4"></div>
                  <div>
                    <p className="font-semibold">Emily Johnson</p>
                    <p className="text-gray-400 text-sm">Casual Bettor</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gray-800/30 backdrop-blur-sm border-gray-700">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6">
                  "Great odds, smooth deposits, and friendly competition. Rivals makes betting social and stress-free."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gray-700 mr-4"></div>
                  <div>
                    <p className="font-semibold">Marcus Lee</p>
                    <p className="text-gray-400 text-sm">Power User</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-96 bg-gradient-to-r from-emerald-500/20 via-blue-500/20 to-emerald-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl mx-auto text-center bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-12">
            <h2 className="text-4xl font-bold mb-6">Ready to Join the Action?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Sign up today and start betting with friends and experience
              sports in a whole new way.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 group"
              onClick={()=>{navigate("/signin")}}
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 border-t border-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <Gamepad2 className="w-6 h-6 text-emerald-400" />
                <span className="text-xl font-bold">
                  Rivals <span className="text-emerald-400">Sportsbook</span>
                </span>
              </div>
              <p className="text-gray-400">The next generation of social sports betting.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/home" className="text-gray-400 hover:text-white transition-colors">Home</Link>
                </li>
                <li>
                  <a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a>
                </li>
                <li>
                  <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</a>
                </li>
                <li>
                  <a href="#testimonials" className="text-gray-400 hover:text-white transition-colors">Testimonials</a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</Link>
                </li>
                <li>
                  <span className="text-gray-500">License</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <ul className="space-y-2">
                <li>
                  <a href="https://x.com/rivalssportbook" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Twitter</a>
                </li>
                <li>
                  <a href="https://www.instagram.com/rivalssportsbook/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Instagram</a>
                </li>
                <li>
                  <a href="https://www.tiktok.com/@rivals.sportsbook" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">Tik Tok</a>
                </li>
                <li>
                  <a href="mailto:rivalssportbook@gmail.com" className="text-gray-400 hover:text-white transition-colors">Email: rivalssportbook@gmail.com</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© {new Date().getFullYear()} Rivals Sportsbook. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

